import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import { 
  CreditCard as CardIcon,
  LocalGasStation as LocalGasStationIcon,
  LocalCarWash as LocalCarWashIcon,
} from "@mui/icons-material";
import { API_URL } from "../../utils/api-config";

interface FuelCard {
  _id: string;
  cardNumber: string;
  provider: string;
  type: string;
  category?: 'carburant' | 'lavage';
  expiryDate: string;
  isActive: boolean;
  isConnected: boolean;
  externalId?: string;
  assignedTo?: string;
  vehiclePlate?: string;
}

interface Props {
  onSelectCard?: (card: FuelCard) => void;
  categoryFilter?: 'all' | 'carburant' | 'lavage';
}

export default function FuelCardList({ onSelectCard, categoryFilter = 'all' }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [cards, setCards] = useState<FuelCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/fuel-cards`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        const data = await res.json();
        // S'assurer que data est un tableau
        if (Array.isArray(data)) {
          setCards(data);
        } else if (data && Array.isArray(data.cards)) {
          setCards(data.cards);
        } else if (data && Array.isArray(data.data)) {
          setCards(data.data);
        } else {
          setCards([]);
        }
        setError(null);
      } catch {
        setError("Erreur lors du chargement des cartes");
        setCards([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCards();
  }, []);

  // Filtrer par catégorie (avec protection si cards n'est pas un tableau)
  const filteredCards = Array.isArray(cards) ? cards.filter(card => {
    if (categoryFilter === 'all') return true;
    const cardCategory = card.category || 'carburant';
    return cardCategory === categoryFilter;
  }) : [];

  // Icône selon la catégorie
  const getCategoryIcon = (category?: string) => {
    if (category === 'lavage') {
      return <Box component="img" src="/lavage-icon.png" sx={{ width: 24, height: 24 }} />;
    }
    return <Box component="img" src="/carburant-icon.png" sx={{ width: 24, height: 24 }} />;
  };

  if (loading)
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Typography color="error">{error}</Typography>;

  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {filteredCards.map((card) => (
          <Card 
            key={card._id} 
            elevation={1} 
            sx={{ borderRadius: 3, borderLeft: card.category === 'lavage' ? '4px solid #03a9f4' : '4px solid #1976d2' }}
            onClick={() => {
              if (onSelectCard) onSelectCard(card);
            }}
          >
            <CardContent sx={{ pb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {getCategoryIcon(card.category)}
                  <Typography variant="h6" fontWeight="bold">
                    {card.cardNumber}
                  </Typography>
                </Box>
                <Chip
                  label={card.isActive ? "Active" : "Inactive"}
                  color={card.isActive ? "success" : "default"}
                  size="small"
                />
              </Box>
              
              <Divider sx={{ my: 1 }} />
              
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Fournisseur</Typography>
                  <Typography variant="body2">{card.provider}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body2">{card.type}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Expiration</Typography>
                  <Typography variant="body2">{new Date(card.expiryDate).toLocaleDateString("fr-FR")}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Connectée</Typography>
                  <Typography variant="body2">{card.isConnected ? "Oui" : "Non"}</Typography>
                </Box>
                <Box sx={{ gridColumn: "span 2" }}>
                  <Typography variant="caption" color="text.secondary">Assignée à</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {card.assignedTo ? `Chauffeur: ${card.assignedTo}` : card.vehiclePlate ? `Véhicule: ${card.vehiclePlate}` : "Non assignée"}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end", p: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onSelectCard) onSelectCard(card);
                }}
              >
                Détails
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        borderRadius: 2,
        overflow: "auto",
        boxShadow: 1,
        "& .MuiTableCell-root": {
          whiteSpace: "nowrap",
          px: { xs: 1, sm: 2 },
        },
        "& .MuiTableCell-head": {
          backgroundColor: "grey.100",
          fontWeight: "bold",
        },
      }}
    >
      <Table stickyHeader size="small" sx={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell>Numéro</TableCell>
            <TableCell>Fournisseur</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Catégorie</TableCell>
            <TableCell>Expiration</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Chauffeur</TableCell>
            <TableCell>Véhicule</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredCards.map((card) => (
            <TableRow
              key={card._id}
              hover
              onClick={() => {
                if (onSelectCard) onSelectCard(card);
              }}
              sx={{ cursor: "pointer" }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getCategoryIcon(card.category)}
                  {card.cardNumber}
                </Box>
              </TableCell>
              <TableCell>{card.provider}</TableCell>
              <TableCell>{card.type}</TableCell>
              <TableCell>
                <Chip
                  label={card.category === 'lavage' ? 'Lavage' : 'Carburant'}
                  color={card.category === 'lavage' ? 'info' : 'primary'}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                {new Date(card.expiryDate).toLocaleDateString("fr-FR")}
              </TableCell>
              <TableCell>
                <Chip
                  label={card.isActive ? "Active" : "Inactive"}
                  color={card.isActive ? "success" : "default"}
                  size="small"
                />
              </TableCell>
              <TableCell>{card.assignedTo || "-"}</TableCell>
              <TableCell>{card.vehiclePlate || "-"}</TableCell>
              <TableCell>
                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onSelectCard) onSelectCard(card);
                  }}
                >
                  Détails
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
